import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, UsersThree, Check } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AssignEmployeesModal = ({ projectId, projectName, currentAssignedIds = [], onClose, onSaved }) => {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(new Set(currentAssignedIds));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/employees`, { withCredentials: true });
      setEmployees(res.data || []);
    } catch {
      toast.error('Impossible de charger la liste des salaries');
    }
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  useEffect(() => {
    setSelected(new Set(currentAssignedIds));
  }, [currentAssignedIds]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(filtered.map(e => e.id)));
  const clearAll = () => setSelected(new Set());

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/projects/${projectId}/assignments`,
        { user_ids: Array.from(selected) },
        { withCredentials: true }
      );
      toast.success(`${selected.size} salarie(s) affecte(s) au chantier`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const filtered = employees.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.name || '').toLowerCase().includes(s) || (e.email || '').toLowerCase().includes(s) || (e.job_title || '').toLowerCase().includes(s);
  });

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      data-testid="assign-employees-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: '8px', width: '100%', maxWidth: '620px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <UsersThree size={22} weight="bold" color="#002FA7" />
            Effectif du chantier
          </h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E5F66' }} aria-label="Fermer"><X size={22} /></button>
        </div>

        <div style={{ padding: '14px 20px', borderBottom: '1px solid #F4F5F7', color: '#5E5F66', fontSize: '0.88rem' }}>
          Selectionnez les salaries affectes a <b>{projectName}</b>. Les salaries coches pourront pointer sur ce chantier.
        </div>

        <div style={{ padding: '12px 20px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid #F4F5F7' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Rechercher un salarie..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="assign-search"
            style={{ flex: '1 1 240px', minWidth: '200px' }}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={selectAll} data-testid="assign-select-all">Tout cocher</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={clearAll} data-testid="assign-clear-all">Tout decocher</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#5E5F66' }}>Aucun salarie trouve</div>
          ) : (
            filtered.map(e => {
              const checked = selected.has(e.id);
              return (
                <label
                  key={e.id}
                  htmlFor={`assign-cb-${e.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 12px', cursor: 'pointer', borderRadius: '6px',
                    backgroundColor: checked ? '#E8F0FE' : 'transparent',
                    border: '1px solid ' + (checked ? '#002FA7' : 'transparent'),
                    marginBottom: '4px',
                    transition: 'background-color 0.12s'
                  }}
                  data-testid={`assign-row-${e.id}`}
                >
                  <input
                    id={`assign-cb-${e.id}`}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(e.id)}
                    data-testid={`assign-cb-${e.id}`}
                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#002FA7' }}
                  />
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: checked ? '#002FA7' : '#F4F5F7', color: checked ? 'white' : '#5E5F66', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem' }}>
                    {(e.name || '?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#1E2030' }}>{e.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#5E5F66' }}>
                      {e.job_title || 'Salarie'}
                      {e.daily_rate ? ` · ${e.daily_rate} EUR/j` : ''}
                    </div>
                  </div>
                  {checked && <Check size={18} weight="bold" color="#002FA7" />}
                </label>
              );
            })
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ color: '#5E5F66', fontSize: '0.88rem' }}>
            <b style={{ color: '#002FA7' }}>{selected.size}</b> salarie(s) selectionne(s)
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving} data-testid="assign-cancel">Annuler</button>
            <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving} data-testid="assign-save">
              {saving ? 'Enregistrement...' : 'Enregistrer l\'effectif'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
